import * as React from 'react';

import {
  REALTIME_PRESENCE_URL,
  useDebouncedCallback,
  useRealtimePresence,
  useRealtimeSubscription,
} from '@klareospace/pharos-lib-front-utils';
import { Card, CardContent, PageHeader, Tabs, TabsList, TabsTrigger } from '@klareospace/pharos-lib-web-utils';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { MainSelector } from '&/features/main-selector';
import { OrderCreationDrawer } from '&/features/order-creation';

import { orderKeys } from '../api';
import { NewOrderButton, OrderDetailDrawer, OrdersActionBar, OrdersTable } from '../components';
import { useOrdersPage, useOrdersTableColumns } from '../hooks';

const MESSENGER_API_CLIENT = 'messenger';
const ORDERS_PAGE_KEY = 'orders-list';
const REALTIME_REFETCH_DEBOUNCE_MS = 500;

// ─── Component ────────────────────────────────────────────────────────────────

export default function OrdersPage(): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('orders');
  const pageState = useOrdersPage();
  const columnsState = useOrdersTableColumns({ onOpenDetailDrawer: pageState.onOpenDetailDrawer });
  const queryClient = useQueryClient();

  const debouncedRefetch = useDebouncedCallback(() => {
    void queryClient.invalidateQueries({ queryKey: orderKeys.all, refetchType: 'active' });
  }, REALTIME_REFETCH_DEBOUNCE_MS);

  useRealtimePresence({
    apiClientName: MESSENGER_API_CLIENT,
    page: ORDERS_PAGE_KEY,
    presenceUrl: REALTIME_PRESENCE_URL,
  });

  useRealtimeSubscription({
    handler: (): void => {
      debouncedRefetch();
    },
    resource: 'orders',
  });

  const pagination = {
    currentPage: pageState.currentPage,
    onPageChange: pageState.onPageChange,
    onPageSizeChange: pageState.onPageSizeChange,
    pageSize: pageState.pageSize,
    pageSizeOptions: pageState.pageSizeOptions,
    totalItems: pageState.totalItems,
    totalPages: pageState.totalPages,
  };

  const columnReorder = {
    onOrderChange: columnsState.onColumnOrderChange,
    order: columnsState.columnOrder,
  };

  const sorting = {
    onSort: pageState.onSort,
    sortBy: pageState.sortBy,
    sortDirection: pageState.sortDirection,
  };

  if (pageState.error) {
    throw pageState.error;
  }

  function handleTabValueChange(value: string): void {
    if (value === 'all' || value === 'action_required') {
      pageState.onTabChange(value);
    }
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <OrderDetailDrawer
        onClose={pageState.onCloseDetailDrawer}
        open={pageState.detailDrawerOpen}
        order={pageState.selectedOrder}
      />
      <OrderCreationDrawer onClose={pageState.onCloseCreateDrawer} open={pageState.createDrawerOpen} />
      <PageHeader
        action={<NewOrderButton onCreateOrder={pageState.onOpenCreateDrawer} />}
        breadcrumb={<MainSelector />}
        title={tCommon('navigation.orders')}
        variant="banner"
      />
      <div className="flex flex-1 flex-col gap-4 overflow-hidden px-6 pb-6 pt-4">
        <Tabs onValueChange={handleTabValueChange} value={pageState.activeTab}>
          <TabsList>
            <TabsTrigger className="cursor-pointer" value="all">
              {t('tabs.all')}
            </TabsTrigger>
            <TabsTrigger className="cursor-pointer" value="action_required">
              {t('tabs.actionRequired')}
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <OrdersActionBar
          activeTab={pageState.activeTab}
          canListDrivers={pageState.canListDrivers}
          canResetColumns={columnsState.canResetColumns}
          carrierFilter={pageState.carrierFilter}
          carrierOptions={pageState.carrierOptions}
          columns={columnsState.allColumns}
          dateRange={pageState.dateRange}
          driverActiveOptions={pageState.driverActiveOptions}
          driverExtraSelectedOptions={pageState.driverExtraSelectedOptions}
          driverFilter={pageState.driverFilter}
          driverHiddenCount={pageState.driverHiddenCount}
          driverInactiveOptions={pageState.driverInactiveOptions}
          hiddenColumns={columnsState.hiddenColumns}
          isDriverOptionsLoading={pageState.isDriverOptionsLoading}
          isDriverSearchActive={pageState.isDriverSearchActive}
          onCarrierChange={pageState.onCarrierChange}
          onDateRangeChange={pageState.onDateRangeChange}
          onDriverChange={pageState.onDriverChange}
          onDriverDropdownOpenChange={pageState.onDriverDropdownOpenChange}
          onDriverSearchChange={pageState.onDriverSearchChange}
          onOrderTypeChange={pageState.onOrderTypeChange}
          onReset={pageState.onReset}
          onResetColumns={columnsState.resetToDefault}
          onSaveColumns={columnsState.saveColumns}
          onSearchChange={pageState.onSearchChange}
          onShipperChange={pageState.onShipperChange}
          onStatusBucketChange={pageState.onStatusBucketChange}
          onToggleColumn={columnsState.toggleColumn}
          orderTypeFilter={pageState.orderTypeFilter}
          orderTypeOptions={pageState.orderTypeOptions}
          searchValue={pageState.searchValue}
          shipperFilter={pageState.shipperFilter}
          shipperOptions={pageState.shipperOptions}
          showCarrierFilter={pageState.showCarrierFilter}
          statusBucketFilter={pageState.statusBucketFilter}
        />
        <Card className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl shadow-sm">
          <CardContent className="flex min-h-0 flex-1 flex-col p-0">
            <OrdersTable
              columnReorder={columnReorder}
              columns={columnsState.visibleColumns}
              isLoading={pageState.isLoading}
              orders={pageState.orders}
              pagination={pagination}
              sorting={sorting}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
